/**
 * <split-flap-text>: a departure-board row of tiles that flips through a list of words.
 * Each changed tile rattles through a few random glyphs before landing, left to right.
 * Pauses off-screen and in background tabs; with reduced motion it just swaps the word.
 * Every time the board comes into view it starts over: blank tiles flipping to the first word.
 * With data-fit="previous" the tiles are sized so the board spans the text of the element
 * right before it (e.g. a heading), re-measured whenever that element resizes.
 * With data-countdown (an ISO date with its UTC offset) the board is a countdown instead:
 * days : hours : minutes : seconds, each changed digit flipping once per second like a flip clock,
 * with optional captions (data-labels). It stops at all zeros.
 * Markup and settings come from blocks/split-flap.liquid.
 */
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
// A countdown rattles through digits only: a letter flashing on a clock reads as a glitch.
const DIGITS = '0123456789';

class SplitFlapText extends HTMLElement {
  connectedCallback() {
    if (this.tiles) return;

    this.countdownTo = this.dataset.countdown ? Date.parse(this.dataset.countdown) : NaN;
    this.words = JSON.parse(this.dataset.words || '[]').map((word) => String(word).toUpperCase());
    if (!this.words.length && Number.isNaN(this.countdownTo)) return;

    this.width = Math.max(...this.words.map((word) => word.length));
    this.align = this.dataset.align || 'center';
    this.holdMs = Number(this.dataset.hold) || 4500;
    this.flipMs = Number(this.dataset.flip) || 110;
    this.staggerMs = Number(this.dataset.stagger) || 45;
    this.flips = Number(this.dataset.flips) || 5;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.board = this.querySelector('[data-board]');
    this.label = this.querySelector('[data-label]');
    if (!Number.isNaN(this.countdownTo)) {
      this.#buildCountdown();
    } else {
      this.tiles = Array.from({ length: this.width }, () => this.#createTile());
      this.board.replaceChildren(...this.tiles.map((tile) => tile.root));
    }
    if (this.dataset.fit === 'previous') this.#fitToPrevious();

    this.index = 0;
    this.shown = ' '.repeat(this.width);
    this.visible = false;
    this.timers = new Set();

    this.observer = new IntersectionObserver((entries) => {
      const wasVisible = this.visible;
      this.visible = entries.some((entry) => entry.isIntersecting);
      if (this.visible && !wasVisible) this.#restart();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.timers?.forEach(clearTimeout);
  }

  /* Tile width is 0.78em, so the font size that fills a given width is known up front. */
  #fitToPrevious() {
    let target = this.previousElementSibling;
    // Skip scripts, styles and text-less decoration (e.g. a divider) to reach the heading.
    while (target && (['SCRIPT', 'STYLE', 'LINK'].includes(target.tagName) || !target.textContent.trim())) {
      target = target.previousElementSibling;
    }
    if (!target) return;

    const fit = () => {
      const range = document.createRange();
      range.selectNodeContents(target);
      const textWidth = range.getBoundingClientRect().width;
      if (!textWidth) return;
      const gap = parseFloat(getComputedStyle(this.board).columnGap) || 0;
      const size = (textWidth - gap * (this.width - 1)) / (this.width * 0.78);
      this.style.setProperty('--split-flap-size', `${Math.max(6, size).toFixed(2)}px`);
    };

    fit();
    document.fonts?.ready.then(fit);
    this.resizeObserver = new ResizeObserver(fit);
    this.resizeObserver.observe(target);
  }

  /* Clear the board and flip from blank to the first word again. */
  #restart() {
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.tiles.forEach((tile) => {
      tile.root.querySelectorAll('.split-flap__flap').forEach((flap) => flap.remove());
      this.#setChar(tile, ' ');
    });
    this.shown = ' '.repeat(this.width);
    if (this.tiles.length === 8 && !Number.isNaN(this.countdownTo)) this.#tick(true);
    else this.#show(0);
  }

  /* Countdown: four groups of two tiles (days, hours, minutes, seconds) with a colon between
     groups and a caption under each. The tiles stay one flat list, so flipping works as for words. */
  #buildCountdown() {
    const labels = JSON.parse(this.dataset.labels || '[]');
    this.width = 8;
    this.tiles = Array.from({ length: 8 }, () => this.#createTile());
    const parts = [];
    for (let g = 0; g < 4; g++) {
      if (g) {
        const colon = document.createElement('span');
        colon.className = 'split-flap__colon';
        colon.textContent = ':';
        parts.push(colon);
      }
      const group = document.createElement('span');
      group.className = 'split-flap__group';
      const row = document.createElement('span');
      row.className = 'split-flap__group-tiles';
      row.append(this.tiles[g * 2].root, this.tiles[g * 2 + 1].root);
      group.append(row);
      if (labels[g]) {
        const caption = document.createElement('span');
        caption.className = 'split-flap__caption';
        caption.textContent = labels[g];
        group.append(caption);
      }
      parts.push(group);
    }
    this.board.classList.add('split-flap__board--countdown');
    this.board.replaceChildren(...parts);
  }

  /* Days (at most 99), hours, minutes and seconds left, as 8 digits. */
  #remaining() {
    let s = Math.max(0, Math.floor((this.countdownTo - Date.now()) / 1000));
    const parts = [Math.min(99, Math.floor(s / 86400)), Math.floor((s %= 86400) / 3600), Math.floor((s %= 3600) / 60), s % 60];
    return { digits: parts.map((n) => String(n).padStart(2, '0')).join(''), parts, done: parts.every((n) => !n) };
  }

  /* One second of the countdown: changed digits flip once (rattle: a few random glyphs first,
     when the board comes into view). Off-screen or in a background tab the digits just update. */
  #tick(rattle = false) {
    const { digits, parts, done } = this.#remaining();
    const [d, h, m] = parts;
    this.label.textContent = `${d} days ${h} hours ${m} minutes`;
    let busy = 0;
    if (!this.visible || document.hidden) {
      this.tiles.forEach((tile, i) => this.#setChar(tile, digits[i]));
      this.shown = digits;
    } else {
      busy = this.#render(digits, rattle ? this.flips : 0);
    }
    if (done) return;
    // On the next whole second, but not before the rattle has landed.
    this.#later(() => this.#tick(), Math.max(busy, 1000 - (Date.now() % 1000) + 20));
  }

  #later(fn, ms) {
    const id = setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
  }

  #pad(word) {
    const space = this.width - word.length;
    if (this.align === 'left') return word.padEnd(this.width, ' ');
    if (this.align === 'right') return word.padStart(this.width, ' ');
    return word.padStart(word.length + Math.floor(space / 2), ' ').padEnd(this.width, ' ');
  }

  #show(index) {
    this.index = index;
    this.label.textContent = this.words[index];
    const duration = this.#render(this.#pad(this.words[index]), this.flips);
    if (this.words.length > 1) this.#later(() => this.#next(), duration + this.holdMs);
  }

  /* Flips every tile whose glyph changes to `target`, each through `flips` random glyphs first,
     left to right. Returns how long the whole board takes. */
  #render(target, flips) {
    let duration = 0;
    this.tiles.forEach((tile, i) => {
      const from = this.shown[i];
      const to = target[i];
      if (from === to) return;

      if (this.reducedMotion) {
        this.#setChar(tile, to);
        return;
      }

      const glyphs = Number.isNaN(this.countdownTo) ? GLYPHS : DIGITS;
      const sequence = Array.from({ length: flips }, () => glyphs[Math.floor(Math.random() * glyphs.length)]);
      sequence.push(to);
      const start = i * this.staggerMs;
      duration = Math.max(duration, start + sequence.length * this.flipMs);

      let previous = from;
      sequence.forEach((char, step) => {
        const next = char;
        const current = previous;
        this.#later(() => this.#flip(tile, current, next), start + step * this.flipMs);
        previous = char;
      });
    });

    this.shown = target;
    return duration;
  }

  #next() {
    if (!this.visible || document.hidden) {
      this.#later(() => this.#next(), 1000);
      return;
    }
    this.#show((this.index + 1) % this.words.length);
  }

  #createTile() {
    const root = document.createElement('span');
    root.className = 'split-flap__tile';
    root.innerHTML =
      '<span class="split-flap__half split-flap__half--top"><span class="split-flap__char"></span></span>' +
      '<span class="split-flap__half split-flap__half--bottom"><span class="split-flap__char"></span></span>';
    const [top, bottom] = root.querySelectorAll('.split-flap__char');
    root.classList.add('is-blank');
    return { root, top, bottom };
  }

  #setChar(tile, char) {
    tile.top.textContent = char;
    tile.bottom.textContent = char;
    tile.root.classList.toggle('is-blank', char === ' ');
  }

  /* The top half already shows the new glyph; the old one falls away as the front flap,
     and the new bottom half swings down as the back flap. */
  #flip(tile, from, to) {
    tile.root.querySelectorAll('.split-flap__flap').forEach((flap) => flap.remove());
    tile.top.textContent = to;
    tile.bottom.textContent = from;
    tile.root.classList.toggle('is-blank', to === ' ');

    const front = document.createElement('span');
    front.className = 'split-flap__flap split-flap__flap--front';
    front.innerHTML = '<span class="split-flap__char"></span>';
    front.firstChild.textContent = from;

    const back = document.createElement('span');
    back.className = 'split-flap__flap split-flap__flap--back';
    back.innerHTML = '<span class="split-flap__char"></span>';
    back.firstChild.textContent = to;

    tile.root.append(front, back);
    this.#later(() => {
      tile.bottom.textContent = to;
      front.remove();
      back.remove();
    }, this.flipMs);
  }
}

if (!customElements.get('split-flap-text')) customElements.define('split-flap-text', SplitFlapText);
